/**
 * Abschnitt „Vorteile“: Nutzen für Kinder und Eltern (Req 5).
 *
 * Jeder Vorteil trägt ein Publikums-Label („Für Kinder“/„Für Eltern“/„Für alle“),
 * damit der Nutzen zusätzlich zur Farbe über Text vermittelt wird (Req 11.5).
 */

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { welcomeColors } from "./welcomeTheme";
import { SectionLabel, scrollToAnchor } from "./welcomeShared";

/** Ein Vorteil — mind. einer für Kinder, einer für Eltern (Req 5.3). */
interface Benefit {
  icon: ReactNode;
  titleKey: string;
  textKey: string;
  audienceKey: string;
}

const BENEFITS: Benefit[] = [
  {
    icon: <ShieldRoundedIcon />,
    titleKey: "welcome.benefit.a.title",
    textKey: "welcome.benefit.a.text",
    audienceKey: "welcome.benefit.a.audience",
  },
  {
    icon: <CalendarMonthRoundedIcon />,
    titleKey: "welcome.benefit.b.title",
    textKey: "welcome.benefit.b.text",
    audienceKey: "welcome.benefit.b.audience",
  },
  {
    icon: <FavoriteRoundedIcon />,
    titleKey: "welcome.benefit.c.title",
    textKey: "welcome.benefit.c.text",
    audienceKey: "welcome.benefit.c.audience",
  },
];

export default function Benefits() {
  const { t } = useI18n();
  const theme = useTheme();
  const colors = welcomeColors(theme.palette.mode);

  return (
    <Container maxWidth="lg">
      <Box id="vorteile" sx={{ pb: { xs: 8, md: 12 }, scrollMarginTop: 88 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
            gap: 5,
            alignItems: "center",
          }}
        >
          <Box>
            <SectionLabel>{t("welcome.benefit.sectionLabel")}</SectionLabel>
            <Typography
              variant="h2"
              component="h2"
              sx={{ fontSize: { xs: 30, md: 40 } }}
            >
              {t("welcome.benefit.sectionTitle")}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: 2, lineHeight: 1.8, fontSize: 17 }}
            >
              {t("welcome.benefit.sectionText")}
            </Typography>
            <Button
              href="#mitmachen"
              onClick={scrollToAnchor}
              variant="contained"
              sx={{ mt: 3, minHeight: 44 }}
              endIcon={<ArrowForwardRoundedIcon />}
            >
              {t("welcome.benefit.cta")}
            </Button>
          </Box>

          <Stack spacing={2}>
            {BENEFITS.map((benefit) => (
              <Paper
                key={benefit.titleKey}
                elevation={0}
                sx={{
                  display: "flex",
                  gap: 2.5,
                  p: 2.5,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 54,
                    height: 54,
                    bgcolor: colors.surfaceSoft,
                    color: "primary.main",
                  }}
                >
                  {benefit.icon}
                </Avatar>
                <Box>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography component="h3" fontWeight={800} fontSize={17}>
                      {t(benefit.titleKey)}
                    </Typography>
                    <Chip
                      label={t(benefit.audienceKey)}
                      size="small"
                      sx={{
                        bgcolor: colors.accentSoft,
                        color: colors.accentText,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                  <Typography
                    color="text.secondary"
                    sx={{ mt: 0.5, lineHeight: 1.7 }}
                  >
                    {t(benefit.textKey)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Box>
    </Container>
  );
}
